import { createSelector } from 'reselect';
import { Receiver } from '../../../types/resources';
import { ApplicationState } from '../../reducers';
import { ReceiverWebsite } from '../../../types/receiver';

const getReceivers = (state: ApplicationState) => state.receiver.receivers.receiverList;
const getReceiverResources = (state: ApplicationState) => state.resources.data.receiver as Receiver;
const getBrands = (state: ApplicationState) => state.parent.brands.brands;
const getTrafficData = (state: ApplicationState) => state.receiver.receivers.trafficData || {};
const getReceiverDetails = (state: ApplicationState) =>
  state.receiver.receivers.receiverDetails || state.settings.companyProfile.companyInfo;

export const mapResourcesToReceivers = createSelector(
  [getReceivers, getReceiverResources, getBrands],
  (receivers, receiverResources, brands) => {
    if (!receiverResources) return [];
    const activeBrands = brands.filter(b => b.active && !b.oe_brand);

    // brand map for O(1) lookup instead of O(n) find
    const brandMap = brands.reduce((map, brand) => {
      map[brand.id] = brand;
      return map;
    }, {} as { [id: number]: (typeof brands)[0] });

    return receivers.map(receiver => {
      // Create a Set of request brand IDs for O(1) lookup instead of O(n) find
      const requestBrandIds = new Set(receiver.requests.map(r => r.brand_id));

      const requests = [
        ...receiver.requests.map(request => ({
          ...request,
          brandName: brandMap[request.brand_id]?.name,
          name: receiver.name,
          receiverId: receiver.receiver_id,
          integrationTypeId: receiver.integration_type_id,
        })),
        ...activeBrands
          .filter(b => !requestBrandIds.has(b.id))
          .map(b => ({
            brand_id: b.id,
            brandName: b.name,
            name: receiver.name,
            receiverId: receiver.receiver_id,
            integrationTypeId: receiver.integration_type_id,
          })),
      ];

      return {
        ...receiver,
        requests,
      };
    });
  }
);

export const getReceiverWebsitesWithTraffic = createSelector(
  [getReceiverDetails, getTrafficData],
  (receiverDetails, trafficData): ReceiverWebsite[] => {
    if (!receiverDetails) return [];

    const baseWebsites =
      receiverDetails.websites ||
      (receiverDetails.website_url ? [{ url: receiverDetails.website_url, isPrimary: true }] : []);

    const receiverTrafficData = trafficData[receiverDetails.id];

    if (receiverTrafficData) {
      return baseWebsites.map(website => ({
        ...website,
        trafficData: receiverTrafficData[website.url] || website.trafficData,
      }));
    }

    return baseWebsites;
  }
);
